<template>
  <div class="full-page" :class="{'nav-open': $sidebar.showSidebar}">
    <notifications></notifications>
    <div class="wrapper wrapper-full-page" @click="toggleSidebarPage">
      <div
        class="page-login"
        :class="setPageClass"
      >
        <div class="page-login--type">
          <md-card>
            <md-card-header>
              <div class="md-title">
                <h1>Select Login Type</h1>
              </div>
            </md-card-header>
            <md-card-content>
              <md-radio v-model="loginType" v-for="type in loginTypes" :key="type.value" :value="type.value" @change="changeLoginType" class="page-login--type__type">
                <div class="content">
                  <img :src="`/img/icons/${type.image}.svg`" :alt="type.icon">
                  <div>
                    <h4>{{ type.title }}</h4>
                    <p>{{ type.description }}</p>
                  </div>
                </div>
              </md-radio>
            </md-card-content>
          </md-card>
          <div class="sticky-note" v-if="!isPrivacyPolicyVisited">
            <p class="pop">We take your privacy seriously and only process your personal information to make your experience better. Using this platform indicates your consent with the processing of your data by eTax and its partners, as detailed in our <a class="pop-lnk static-lnk" href="/privacy-policy">Privacy Policy</a></p>
            <div class="ck-btn">
              <md-button slot="footer" class="md-sm mt-4 md-theme-default pull-right" @click="setCookie">Got it</md-button>
            </div>
          </div>
        </div>
        <div class="page-login--form">
          <zoom-center-transition :duration="pageTransitionDuration" mode="out-in">
            <router-view></router-view>
          </zoom-center-transition>
          <footer class="footer">
            <div class="copyright text-center">&copy; {{ new Date().getFullYear() }} e-Tax. All Rights Reserved.
              &nbsp;
              <a v-if="isPrivacyPolicyVisited" class="static-lnk" href="/privacy-policy">Privacy Policy</a>
            </div>
          </footer>
        </div>
      </div>
      <tax-calculator-new />
    </div>
    <news-popup-v2 v-if="showNewsPopup" />
  </div>
</template>

<script>
import { ZoomCenterTransition } from "vue2-transitions";
import TaxCalculatorNew from "@/components/TaxCalculatorNew";
import NewsPopupV2 from "@/components/News/NewsPopupV2.vue";
import { getCookieByName, setCookieByName } from "@/lib/utils";

export default {
  components: {
    NewsPopupV2,
    TaxCalculatorNew,
    ZoomCenterTransition
  },
  props: {
    backgroundColor: {
      type: String,
      default: "black"
    }
  },
  inject: {
    autoClose: {
      default: true
    }
  },
  data() {
    return {
      loginTypes: [
        {
          image: 'taxpayer',
          title: 'Taxpayer',
          description: 'Login as an individual or corporate taxpayer',
          value: 'login'
        },
        {
          image: 'corporate-admin',
          title: 'Corporate Admin',
          description: 'Manage your company\'s profile',
          value: 'corporate-admin'
        },
        {
          image: 'consultant',
          title: 'Consultant',
          description: 'Login as a consultant',
          value: 'consultant'
        },
        {
          image: 'admin',
          title: 'LIRS Admin',
          description: 'Login as an administrator',
          value: 'admin'
        }
      ],
      loginType: 'login',
      responsive: false,
      showMenu: false,
      showCalculator: false,
      menuTransitionDuration: 250,
      pageTransitionDuration: 300,
      year: new Date().getFullYear(),
      privacyPolicyVisited: false,
      showNewsPopup: false
    };
  },
  computed: {
    setPageClass() {
      return `${this.$route.name}-page`.toLowerCase();
    },

    isPrivacyPolicyVisited() {
      return this.privacyPolicyVisited;
    }
  },
  methods: {
    setCookie() {
      const isCookieSet = setCookieByName('privacyPolicyVisited', 'true');
      if(isCookieSet) this.privacyPolicyVisited = true;
    },
    toggleSidebarPage() {
      if (this.$sidebar.showSidebar) {
        this.$sidebar.displaySidebar(false);
      }
    },
    linkClick() {
      if (
        this.autoClose &&
        this.$sidebar &&
        this.$sidebar.showSidebar === true
      ) {
        this.$sidebar.displaySidebar(false);
      }
    },
    toggleSidebar() {
      this.$sidebar.displaySidebar(!this.$sidebar.showSidebar);
    },
    toggleNavbar() {
      document.body.classList.toggle("nav-open");
      this.showMenu = !this.showMenu;
    },
    closeMenu() {
      document.body.classList.remove("nav-open");
      this.showMenu = false;
    },
    onResponsiveInverted() {
      if (window.innerWidth < 991) {
        this.responsive = true;
      } else {
        this.responsive = false;
      }
    },
    changeLoginType(loginType) {
      this.$router.push(loginType === 'login' ? '/login' : `/${loginType}/login`)
    }
  },
  mounted() {
    this.privacyPolicyVisited = getCookieByName('privacyPolicyVisited');
    this.onResponsiveInverted();
    window.addEventListener("resize", this.onResponsiveInverted);

    if (this.$route && ["/login", "/corporate-admin/login"].includes(this.$route.path)) {
      this.showNewsPopup = true
    }
  },
  created() {
    const types = this.$route.path.split("/");
    this.loginType = types && types.length > 1 ? types[1] : this.$router.push("/login")
  },
  beforeDestroy() {
    this.closeMenu();
    window.removeEventListener("resize", this.onResponsiveInverted);
  },
  beforeRouteUpdate(to, from, next) {
    // Close the mobile menu first then transition to next page
    if (this.showMenu) {
      this.closeMenu();
      setTimeout(() => {
        next();
      }, this.menuTransitionDuration);
    } else {
      next();
    }
  }
};
</script>
<style lang="scss" scoped>
$scaleSize: 0.1;
$zoomOutStart: 0.7;
$zoomOutEnd: 0.46;
@keyframes zoomIn8 {
  from {
    opacity: 0;
    transform: scale3d($scaleSize, $scaleSize, $scaleSize);
  }
  100% {
    opacity: 1;
  }
}
.wrapper-full-page .zoomIn {
  animation-name: zoomIn8;
}
@keyframes zoomOut8 {
  from {
    opacity: 1;
    transform: scale3d($zoomOutStart, $zoomOutStart, $zoomOutStart);
  }
  to {
    opacity: 0;
    transform: scale3d($zoomOutEnd, $zoomOutEnd, $zoomOutEnd);
  }
}
.wrapper-full-page .zoomOut {
  animation-name: zoomOut8;
}
.copyright {
  width: 100%;
  text-align: center;
}

.static-lnk {
  text-decoration: underline;
}
.sticky-note {
  align-items: flex-start;
  animation: php .5s cubic-bezier(.25,1,.5,1) .12s forwards;
  background: #fff;
  bottom: 1rem;
  box-shadow: 0 0.2rem 0.6rem #05162833;
  display: flex;
  flex-direction: column;
  max-width: 24rem;
  overflow: hidden;
  position: fixed;
  left: 1rem;
  width: calc(100vw - 2rem);
  z-index: 1000;
  text-align: justify !important;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  opacity: 0.8;
  border-radius: 4px;
}

.pop {
  color: #333;
}

.pop-lnk {
  color: #333 !important;
  font-weight: 500 !important;
}

.ck-btn {
  width: 100%;
  margin-top: -1rem;
}

@media (min-width: 1024px) {
  .sticky-note {
    bottom: 3rem;
    left: 3rem;
  }
}

@media (min-width: 400px) {
  .sticky-note {
    bottom: 1.5rem;
    padding: 0.3rem;
    left: 1.5rem;
    width: calc(100vw - 3rem);
  }
}


/* New Login */
.page-login {
  display: grid;
  grid-template-columns: 45% 55%;
  height: 100%;
  min-height: 100vh;
  background-position: top center;
  background-size: cover;
  margin: 0;
  padding: 0;
  position: relative;
  background-color: #ffffff;

  > div {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
  }

  /* LEFT SIDE */
  &--type {
    background-color: #0A3B27;
    background-image: url('/img/minimal-pattern.png');
    border: 0.5px solid #EAECF0;

    .md-card {
      max-width: 420px;
      min-height: 350px;
      margin: auto;
      border-radius: 8px;
    }

    .md-card-content {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 12px;
    }

    h1 {
      font-size: 18px;
      font-weight: 500;
      text-align: center;
      color: #344054;
      margin-bottom: 0;
    }

    &__type {
      display: flex;
      flex-direction: row-reverse;
      justify-content: space-between;
      align-items: center;
      position: relative;
      border-radius: 16px;
      background: #f1f8ff;
      margin: 0;

      .content {
        display: grid;
        grid-template-columns: 32px 1fr;
        grid-gap: 15px;
        align-items: center;

        img {
          height: 32px;
          width: 32px;
        }

        h4, p {
          margin: 0;
        }

        h4 {
          font-weight: 500;
          font-size: 15px;
          color: #1B1B1B;
        }

        p {
          color: #8D8D8D;
          font-size: 14px;
        }
      }

      :deep(.md-radio-label) {
        height: auto;
        width: calc(100% - 48px);
        padding: 16px;
      }

      :deep(.md-radio-container) {
        height: 16px;
        width: 16px;
        min-width: 16px;
        right: 16px;

        &:before {
          height: 80px;
        }
      }

      &.md-checked :deep(.md-radio-container) {
        border-color: #20A7EA !important;
      }

      &.md-checked :deep(.md-radio-container::after) {
        background-color: #20A7EA !important;
      }
    }
  }

 /* RIGHT SIDE */
  &--form {
    background: #ffffff;

    position: relative;

    :deep(.md-card .md-card-actions .md-button) {
      margin: 0 !important;
    }

    footer {
      position: absolute;
      bottom: 40px;
      left: 50%;
      transform: translateX(-50%);

      .copyright, a {
        color: #000000 !important;
        font-size: 14px;
      }
    }
  }
}

@media (max-width: 991px) {
  .page-login {
    grid-template-columns: 1fr;

    &--type {
      padding: 30px 5px;
      .md-card {
        max-width: 95% !important;
        min-height: auto;
      }

      .md-card-content {
        display: grid;
        grid-template-columns: 1fr 1fr;
        grid-gap: 15px;
      }
    }
  }
}

@media (max-width: 600px) {
  .page-login {
    &--type {
      padding: 30px 5px;
      .md-card-content {
        grid-template-columns: 1fr 1fr;
        padding: 15px 12px;
      }

      &__type .content {
        grid-gap: 5px !important;

        h4 {
          margin-bottom: 0;
          line-height: 1;
        }
        p {
          display: none;
        }
      }
    }
    &--form {
      padding: 30px 10px 50px;

      footer {
        bottom: -10px !important;
      }
    }
  }
}

.page-login--type .md-card {
  background: #ffffff !important;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15) !important;
  border: none !important;
  border-radius: 8px;
}

.page-login--type .md-card-header {
  background: transparent !important;
  box-shadow: none !important;
  padding-top: 0px !important;
}

.page-login--type .md-card-content {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 12px;
  background-color: #F5F5F5;
  border-radius: 16px;
}

.page-login--type__type {
  background: #ffffff !important;
  border: 1px solid transparent;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.page-login--type__type:hover {
  border-color: #F5F5F5;
  box-shadow: 0 4px 10px rgba(0,0,0,0.06);
}

.page-login--type__type.md-checked {
  border-color: #F5F5F5;
  box-shadow: 0 4px 12px rgba(0,0,0,0.06);
}
</style>
